import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import OpalGrid, { useGridPageState, useGridSortState } from '../../../../organisms/DenaliAnalysis/tables/operational'
import { useListMigrationBatches } from '../../../../../api/documentVault'
import { useFormattingContext } from '../../../../organisms/FormattingProvider/FormattingContext'
import { StatusCell } from '../../shared/BatchComponents'
import { getBatchStatus } from './migrationUtils'

const MigrationBatchList = ({ onSelectBatch }) => {
  const { formatter } = useFormattingContext()
  const { pageState, setPageState } = useGridPageState(25)
  const { sortState, setSortState } = useGridSortState([{ id: 'batchId', desc: true }])

  const sortField = sortState?.[0]?.id ?? 'batchId'
  const sortDir = sortState?.[0]?.desc ? 'desc' : 'asc'
  const { data, isFetching } = useListMigrationBatches({
    take: pageState.pageSize,
    skip: pageState.pageIndex * pageState.pageSize,
    sortField,
    sortDir
  })

  const batches = useMemo(() => data?.batches ?? [], [data])

  const columns = useMemo(() => [
    { accessorKey: 'batchId', header: 'ID', size: 60 },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (row) => row.isProcessing,
      cell: ({ row }) => <StatusCell status={getBatchStatus(row.original)} />,
      size: 100
    },
    {
      id: 'orionFirmId',
      header: 'Orion Firm',
      accessorFn: (row) => row.metadata?.orionFirmId ?? '-',
      size: 100
    },
    {
      id: 'progress',
      header: 'Progress',
      accessorFn: (row) => row.taskSummary?.completed ?? 0,
      cell: ({ row }) => {
        const s = row.original.taskSummary
        if (!s) return '-'
        return (
          <span>
            {s.completed}/{s.total}
            {s.errored > 0 && <span style={{ color: 'var(--color-danger-500)', marginLeft: '4px' }}>({s.errored} errors)</span>}
          </span>
        )
      },
      size: 150
    },
    {
      accessorKey: 'lastStartedDate',
      header: 'Started',
      cell: ({ getValue }) => getValue() ? formatter(getValue(), '&M/D/YY h:mm a') : '-',
      size: 150
    },
    {
      accessorKey: 'lastFinishedDate',
      header: 'Completed',
      cell: ({ getValue }) => getValue() ? formatter(getValue(), '&M/D/YY h:mm a') : '-',
      size: 150
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const batchId = row.original?.batchId
        if (!batchId) return null
        return (
          <button
            type='button'
            className='btn btn-link p-0'
            aria-label={`View batch ${batchId}`}
            onClick={(e) => {
              e.stopPropagation()
              onSelectBatch(batchId)
            }}
          >
            View
          </button>
        )
      },
      size: 80
    }
  ], [formatter, onSelectBatch])

  const handleTableClick = useCallback((e) => {
    const row = e.target.closest('.og-tr')
    if (!row || !row.parentElement?.classList?.contains('og-tbody')) return
    const rowIndex = Array.from(row.parentElement.children).indexOf(row)
    if (rowIndex >= 0 && batches[rowIndex]) {
      onSelectBatch(batches[rowIndex].batchId)
    }
  }, [batches, onSelectBatch])

  return (
    <div>
      <div className='font-weight-bold font-size-lg mb-2'>Migration Batches</div>
      <div onClick={handleTableClick} style={{ cursor: 'pointer' }}>
        <OpalGrid
          data={batches}
          rowCount={data?.total ?? batches.length}
          columns={columns}
          isFetching={isFetching}
          pageState={pageState}
          sortState={sortState}
          onPageChange={setPageState}
          onSortChange={setSortState}
        />
      </div>
    </div>
  )
}

MigrationBatchList.propTypes = {
  onSelectBatch: PropTypes.func.isRequired
}

export default MigrationBatchList
